import React, { useEffect, useState } from 'react';
import {
  Container, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, Button, Box, Alert, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import { certsApi } from '../api';

function Certificates() {
  const [certs, setCerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [feedback, setFeedback] = useState(null);
  const [renewingTarget, setRenewingTarget] = useState('');
  const [issueDialog, setIssueDialog] = useState(false);
  const [newDomain, setNewDomain] = useState('');
  const [newEmail, setNewEmail] = useState('');

  const fetchCerts = async () => {
    try {
      setLoading(true);
      const res = await certsApi.list();
      setCerts(res.data);
      setError('');
    } catch (err) {
      setError('Failed to fetch certificates');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCerts(); }, []);

  const handleRenew = async (domain, force = false) => {
    const target = domain || '__all__';
    try {
      setRenewingTarget(target);
      setFeedback({ severity: 'info', message: `Renewal started for ${domain || 'all domains'}...` });

      const res = await certsApi.renew(domain, { force });
      const payload = res.data || {};
      const details = payload.details || {};
      const action = details.action;
      const summary = details.summary || payload.message;

      setFeedback({
        severity: action === 'no_renewal_needed' ? 'info' : 'success',
        message: summary || `Renewal completed for ${domain || 'all domains'}`,
      });

      await fetchCerts();
    } catch (err) {
      const apiMessage = err?.response?.data?.details?.summary || err?.response?.data?.message;
      setFeedback({
        severity: 'error',
        message: apiMessage || `Renewal failed for ${domain || 'all domains'}`,
      });
      setError(apiMessage || 'Renewal failed');
    } finally {
      setRenewingTarget('');
    }
  };

  const handleIssue = async () => {
    try {
      await certsApi.issue(newDomain, newEmail);
      setIssueDialog(false);
      setNewDomain('');
      setNewEmail('');
      setFeedback({ severity: 'success', message: `Certificate issuance requested for ${newDomain}` });
      await fetchCerts();
    } catch (err) {
      const apiMessage = err?.response?.data?.message;
      setError(apiMessage || 'Certificate issuance failed');
      setFeedback({ severity: 'error', message: apiMessage || 'Certificate issuance failed' });
    }
  };

  const getStatusColor = (cert) => {
    if (!cert.exists && !cert.certExists) return 'error';
    if (!cert.expiry) return 'default';
    const days = Math.floor((new Date(cert.expiry) - new Date()) / 86400000);
    if (days <= 0) return 'error';
    if (days <= 30) return 'warning';
    return 'success';
  };

  const getStatusLabel = (cert) => {
    if (!cert.exists && !cert.certExists) return 'Missing';
    if (!cert.expiry) return 'Unknown';
    const days = Math.floor((new Date(cert.expiry) - new Date()) / 86400000);
    if (days <= 0) return 'Expired';
    if (days <= 30) return `Expiring (${days}d)`;
    return `Valid (${days}d)`;
  };

  if (loading) {
    return <Container sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}><CircularProgress /></Container>;
  }

  return (
    <Container maxWidth="xl">
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4" fontWeight="bold" sx={{ flexGrow: 1 }}>Certificate Management</Typography>
        <Button startIcon={<AddIcon />} variant="contained" onClick={() => setIssueDialog(true)} sx={{ mr: 1 }}>
          Issue New
        </Button>
        <Button
          startIcon={<AutorenewIcon />}
          onClick={() => handleRenew()}
          disabled={!!renewingTarget}
        >
          {renewingTarget === '__all__' ? 'Renewing All...' : 'Renew All'}
        </Button>
        <Button startIcon={<RefreshIcon />} onClick={fetchCerts}>Refresh</Button>
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {feedback?.message && <Alert severity={feedback.severity || 'info'} sx={{ mb: 2 }}>{feedback.message}</Alert>}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Domain</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Expiry</TableCell>
              <TableCell>Auto-Renew</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {certs.map((cert, i) => (
              <TableRow key={i} hover>
                <TableCell>{cert.domain}</TableCell>
                <TableCell><Chip label={getStatusLabel(cert)} color={getStatusColor(cert)} size="small" /></TableCell>
                <TableCell>{cert.expiry ? new Date(cert.expiry).toLocaleDateString() : 'N/A'}</TableCell>
                <TableCell>{cert.autoRenew ? <Chip label="Yes" color="success" size="small" /> : <Chip label="No" size="small" />}</TableCell>
                <TableCell>
                  <Button
                    size="small"
                    onClick={() => handleRenew(cert.domain)}
                    disabled={!!renewingTarget}
                  >
                    {renewingTarget === cert.domain ? 'Renewing...' : 'Renew'}
                  </Button>
                  <Button
                    size="small"
                    color="warning"
                    onClick={() => handleRenew(cert.domain, true)}
                    disabled={!!renewingTarget}
                  >
                    Force Renew
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {certs.length === 0 && (
              <TableRow>
                <TableCell colSpan={5}>
                  <Typography color="text.secondary" textAlign="center" sx={{ py: 2 }}>
                    No certificates found. Issue a new certificate to get started.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={issueDialog} onClose={() => setIssueDialog(false)}>
        <DialogTitle>Issue New Certificate</DialogTitle>
        <DialogContent>
          <TextField label="Domain" fullWidth margin="normal" value={newDomain} onChange={e => setNewDomain(e.target.value)} placeholder="server.richardmeasey.com" />
          <TextField label="Email" fullWidth margin="normal" value={newEmail} onChange={e => setNewEmail(e.target.value)} placeholder="admin@example.com" />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIssueDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleIssue} disabled={!newDomain}>Issue</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
export default Certificates;
